import { useMemo } from "react";
import { normalizeSlotLabel, sanitizeSlotLabelInput } from "@/lib/manufacturing/slotLabel";
import { mfgInlineInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

function findTabCompletion(typed: string, suggestions: readonly string[]): string | null {
  const q = sanitizeSlotLabelInput(typed);
  if (!q) return null;
  const normalized = [
    ...new Set(suggestions.map((s) => normalizeSlotLabel(s)).filter(Boolean)),
  ].sort((a, b) => a.localeCompare(b));
  return normalized.find((s) => s.startsWith(q) && s !== q) ?? null;
}

/** Inline slot-label input with ghost completion; Tab accepts. */
export function SlotLabelTabCompleteInput({
  value,
  onChange,
  suggestions,
  placeholder = "e.g. band_board",
  disabled,
  id,
}: {
  value: string;
  onChange: (next: string) => void;
  suggestions: readonly string[];
  placeholder?: string;
  disabled?: boolean;
  id?: string;
}) {
  const completion = useMemo(
    () => findTabCompletion(value, suggestions),
    [value, suggestions],
  );
  const ghostSuffix =
    completion && value.length > 0 && completion.startsWith(value)
      ? completion.slice(value.length)
      : "";

  return (
    <div className="relative w-full">
      {completion && ghostSuffix ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center px-3 py-2 text-sm"
        >
          <span className="whitespace-pre text-transparent">{value}</span>
          <span className="whitespace-pre text-gray-400 dark:text-zinc-500">{ghostSuffix}</span>
        </div>
      ) : null}
      <input
        id={id}
        className={`${mfgInlineInputClass} relative w-full bg-transparent`}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => onChange(sanitizeSlotLabelInput(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === "Tab" && completion) {
            e.preventDefault();
            onChange(completion);
          }
        }}
        onBlur={() => {
          const n = normalizeSlotLabel(value);
          if (n !== value) onChange(n);
        }}
      />
      {completion ? (
        <span className="mt-0.5 block text-[10px] text-gray-500 dark:text-zinc-400">
          Tab to accept <span className="font-medium">{completion}</span>
        </span>
      ) : null}
    </div>
  );
}
